import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { Disclaimer } from "../components/Disclaimer";
import { LocationEditor } from "../components/LocationEditor";
import { Send, MessageSquare, ScanLine, Ruler, Compass, Download, Trash2, Mic, Square } from "lucide-react";
import { Linkify } from "../components/Linkify";
import { toast } from "sonner";

const STARTERS = [
  "I have $0 and 5 hours a week. Where do I start?",
  "What's something people near me actually pay for?",
  "Is flipping furniture worth it here?",
  "How do I make money with a truck?",
];

export default function Ask() {
  const { user, usage, refreshUsage } = useAuth();
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const chatEnd = useRef(null);

  const mentorLocked = user?.paid && usage?.applies && !usage.mentor_available;

  useEffect(() => { api.get("/ask").then((r) => setMessages(r.data)); }, []);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, sending]);

  const send = async (msg) => {
    const t = (msg ?? text).trim();
    if (!t) return;
    if (mentorLocked) { toast.error("You're out of coins — buy more to keep chatting."); return; }
    setText(""); setSending(true);
    setMessages((m) => [...m, { id: "tmp", role: "user", text: t }]);
    try {
      const { data } = await api.post("/ask", { text: t });
      setMessages((m) => [...m.filter((x) => x.id !== "tmp"), data.userMessage, data.mentorMessage]);
      refreshUsage();
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== "tmp"));
      toast.error(apiError(e.response?.data?.detail));
      refreshUsage();
    }
    finally { setSending(false); }
  };

  const saveChat = async () => {
    if (messages.length === 0) { toast.error("No chat to save yet"); return; }
    const { data } = await api.get("/ask/text");
    const blob = new Blob([data.text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = data.filename; a.click();
    URL.revokeObjectURL(url);
    toast.success("Chat saved as .txt");
  };

  const clearChat = async () => {
    if (messages.length === 0) return;
    if (!window.confirm("Delete your entire chat history? This cannot be undone.")) return;
    try { await api.delete("/ask"); setMessages([]); toast.success("Chat history deleted"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const toggleMic = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false); setTranscribing(true);
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const { data } = await api.post("/stt/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) setText((t) => (t ? t + " " : "") + data.text);
          else toast.message("Didn't catch that — try again.");
        } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
        finally { setTranscribing(false); }
      };
      recRef.current = mr; mr.start(); setRecording(true);
    } catch { toast.error("Microphone not available."); }
  };

  return (
    <Layout>
      <div className="fadeup space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-head font-bold text-2xl sm:text-3xl flex items-center gap-2" style={{ color: "#fff" }}>
              <MessageSquare className="w-6 h-6" /> Talk it through
            </h2>
            <p className="text-sm mt-1" style={{ color: "#cbd5e1" }}>
              Just talk to the mentor. No path needed — ask anything about putting time or money into something{user?.city ? ` in ${user.city}` : ""}.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={saveChat} className="pm-btn-ghost text-sm flex items-center gap-1.5" style={{ background: "rgba(255,255,255,0.08)", color: "#fff", borderColor: "rgba(255,255,255,0.35)" }} data-testid="ask-save-btn"><Download className="w-4 h-4" /> Save</button>
            <button onClick={clearChat} className="pm-btn-ghost text-sm flex items-center gap-1.5" style={{ background: "rgba(255,255,255,0.08)", color: "#fff", borderColor: "rgba(255,255,255,0.35)" }} data-testid="ask-clear-btn"><Trash2 className="w-4 h-4" /> Delete</button>
            <button onClick={() => nav("/scan")} className="pm-btn-ghost text-sm flex items-center gap-1.5" style={{ background: "rgba(255,255,255,0.08)", color: "#fff", borderColor: "rgba(255,255,255,0.35)" }}><ScanLine className="w-4 h-4" /> Scan</button>
            <button onClick={() => nav("/measure")} className="pm-btn-ghost text-sm flex items-center gap-1.5" style={{ background: "rgba(255,255,255,0.08)", color: "#fff", borderColor: "rgba(255,255,255,0.35)" }}><Ruler className="w-4 h-4" /> Measure</button>
          </div>
        </div>

        {!user?.city && <LocationEditor />}

        <div className="flex flex-col" style={{ height: "66vh", background: "rgba(10,12,20,0.55)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.18)" }} data-testid="ask-panel">
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && !sending && (
              <div className="h-full flex flex-col items-center justify-center text-center px-6">
                <Compass className="w-8 h-8 mb-3" style={{ color: "#93c5fd" }} />
                <p className="text-sm mb-4" style={{ color: "#e2e8f0" }}>Not sure where to begin? Try one of these.</p>
                <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                  {STARTERS.map((s, i) => (
                    <button key={i} onClick={() => send(s)} className="text-sm text-left px-3 py-2" style={{ background: "rgba(255,255,255,0.08)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)" }} data-testid={`ask-starter-${i}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ask-msg-${m.role}`}>
                <div className="max-w-[85%] p-3 text-sm" style={m.role === "user"
                  ? { background: "#2563eb", color: "#fff" }
                  : { background: "rgba(255,255,255,0.94)", color: "#0a0a0a", border: "1px solid rgba(255,255,255,0.4)" }}>
                  <Linkify text={m.text} linkStyle={m.role === "user" ? { color: "#dbeafe" } : undefined} />
                </div>
              </div>
            ))}
            {sending && <div className="label-mono" style={{ color: "#cbd5e1" }}>Mentor is thinking…</div>}
            <div ref={chatEnd} />
          </div>
          <div className="p-3 flex gap-2" style={{ borderTop: "1px solid rgba(255,255,255,0.18)" }}>
            <button onClick={toggleMic} disabled={transcribing || mentorLocked} className="pm-btn px-3" title="Speak" data-testid="ask-mic-btn"
              style={recording ? { background: "var(--warning)", borderColor: "var(--warning)" } : {}}>
              {transcribing ? "…" : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <input className="pm-input text-sm" placeholder={mentorLocked ? "Out of coins — buy more to chat" : (recording ? "Listening…" : "Ask the mentor anything…")} value={text}
              disabled={mentorLocked}
              onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="ask-input" />
            <button onClick={() => send()} disabled={sending || mentorLocked} className="pm-btn px-3" data-testid="ask-send-btn"><Send className="w-4 h-4" /></button>
          </div>
          {mentorLocked && (
            <div className="px-3 pb-3 text-xs" style={{ color: "#fca5a5" }} data-testid="ask-locked-note">
              You're out of coins. Buy 50 more (top of the page) or wait for your monthly refresh.
            </div>
          )}
        </div>

        <Disclaimer compact />
      </div>
    </Layout>
  );
}
